import React from 'react';
import { PropTypes as PT } from 'prop-types';
import { Route, Switch, Redirect, withRouter } from 'react-router-dom';
import { Provider, connect } from 'react-redux';
import { localizationSelectors } from '@lifesize/nucleus';
import ConnectedIntlProvider from 'containers/ConnectedIntlProvider/ConnectedIntlProviderContainer';
import Base from 'layouts/BaseLayout/BaseLayout';
import Core from 'layouts/CoreLayout/CoreLayout';
import Auth from 'containers/AuthLayout/AuthLayoutContainer';
import HomeContainer from 'containers/Home/HomeContainer';
import ChatContainer from 'containers/Chat/ChatContainer/ChatContainer';
import ContactsContainer from 'containers/Contacts/ContactsContainer';
import MeetingsContainer from 'containers/Meetings/MeetingsContainer';
import RoomSystemsContainer from 'containers/RoomSystems/RoomSystemsContainer';
import UpcomingContainer from 'containers/Upcoming/UpcomingContainer';
import AuthContainer from 'containers/Auth/AuthContainer';
import JoinPageContainer from 'containers/JoinPage/JoinPageContainer';
import requireAuthentication from 'containers/Authenticated/AuthenticatedContainer';
import { routes } from 'constants/Application';
import queryString from 'query-string';
import _get from 'lodash/get';
import { setCurrentPath } from '../actions/Auth';
import Logger from 'utils/logger';
import { ThemeProvider } from '@material-ui/core';
import { MUIComponentsTheme } from '@lifesize/clients.mui-components';
import { connectionEventManager } from 'utils/connectionEventManager';

class AppContainer extends React.PureComponent {
  componentDidMount() {
    // Check for a query string with an action
    if (window.location.search) {
      const query = queryString.parse(window.location.search);
      if (query.action === 'join' && query.extension) {
        this.props.history.push(`/join/${query.extension}?name=${query.name}&email=${query.email}`);
      }
      if (query.action === 'start') {
        this.props.history.push(routes.startRoute);
      }
    }

    // Check for a hash for subfolder routing
    if (window.location.hash.includes('reroute')) {
      const hashRoute = window.location.hash.replace('#reroute', '');
      this.props.history.push(hashRoute);
    }

    // TODO WEBTOP-2272 force fallback to polyfill until react-measure plays nice with Chrome 64
    window.BackupResizeObserver = window.ResizeObserver;
    window.ResizeObserver = undefined;

    window.addEventListener('offline', () => this.props.offline());
    window.addEventListener('online', () => this.props.online());
    /*
      Listen for location change and store it in redux store so that webclient works in Chrome and electron.
      Window.location.pathName will break in electron where webmodule uses MemoryRouter.
      for now redux store will have currentPath, history object can be added in store if it is needed in future.
     */
    this.props.history.listen(action => {
      this.props.setCurrentPath(action.pathname || '/');
    });
  }

  componentWillUnmount() {
    window.removeEventListener('offline');
    window.removeEventListener('online');
  }

  renderCoreRoute = Component => (
    <Core>
      <Component />
    </Core>
  );

  renderJoinPageRoute = (query) => {
    const props = query && query.extension ? {
      extension: query.extension,
      name: query.name
    } : {};
    return (
      <Auth>
        <JoinPageContainer {...props} />
      </Auth>
    );
  }


  renderAuthRoute = (query) => {
    // skip Auth Container for JoinRoute
    if (query && query.action === 'join' && query.extension) {
      return this.renderJoinPageRoute(query);
    }

    return (
      <Auth>
        <AuthContainer />
      </Auth>
    );
  }

  render() {
    const { store } = this.props;
    const locale = _get(localizationSelectors.localization(store.getState()), 'languageId');
    let query;

    try {
      // attempt to parse query string for join flow page
      if (this.props.location && this.props.location.search) {
        query = queryString.parse(this.props.location.search);
      }

      // also attempt to parse pathname to find previous query params for join flow
      const paths = _get(this, 'props.location.pathname', '').split('/').filter(p => !!p);
      if (paths && paths.length > 1 && paths[0] === 'join') {
        query.action = 'join';
        query.extension = paths[1];
      }
    } catch (ex) {
      // fall through to regular auth route
      Logger.info('Unable to parse queryString');
    }

    return (
      <Provider store={store}>
        <ConnectedIntlProvider key={locale}>
          <ThemeProvider theme={MUIComponentsTheme}>
            <Base>
              <Switch>
                <Route
                  exact
                  path={routes.base}
                  render={() => this.renderCoreRoute(requireAuthentication(HomeContainer))}
                />
                <Route
                  path={routes.home}
                  render={() => <Redirect push to={'/'} />}
                />
                <Route
                  path={routes.chat}
                  render={() => this.renderCoreRoute(requireAuthentication(ChatContainer))}
                />
                <Route
                  path={`${routes.contacts}/:selectedUuid?`}
                  render={() => this.renderCoreRoute(requireAuthentication(ContactsContainer))}
                />
                <Route
                  path={`${routes.meetings}/:selectedUuid?`}
                  render={() => this.renderCoreRoute(requireAuthentication(MeetingsContainer))}
                />
                <Route
                  path={`${routes.roomSystems}/:selectedUuid?`}
                  render={() => this.renderCoreRoute(requireAuthentication(RoomSystemsContainer))}
                />
                <Route
                  path={routes.upcoming}
                  render={() => this.renderCoreRoute(requireAuthentication(UpcomingContainer))}
                />
                <Route
                  path={routes.auth}
                  render={() => this.renderAuthRoute(query)}
                />
                <Route
                  path={routes.startRoute}
                  render={() => this.renderCoreRoute(requireAuthentication(HomeContainer))}
                />
                <Route
                  path={routes.join}
                  render={() => this.renderJoinPageRoute(query)}
                />
                <Route path={routes.desktopLogout} render={() => null} />
              </Switch>
            </Base>
            </ThemeProvider>
        </ConnectedIntlProvider>
      </Provider>
    );
  }
}

AppContainer.propTypes = {
  store: PT.object.isRequired,
  offline: PT.func.isRequired,
  online: PT.func.isRequired,
  setCurrentPath: PT.func.isRequired,
};

function mapDispatchToProps(dispatch) {
  return {
    online: () => connectionEventManager.networkOnlineEvent(),
    offline: () => connectionEventManager.networkOfflineEvent(),
    setCurrentPath: (path) => dispatch(setCurrentPath(path))
  };
}

export default withRouter(connect(null, mapDispatchToProps)(AppContainer));
